import { collection, documentId, getDocs, query, where } from "firebase/firestore";
import geohash from "ngeohash";
import { db } from "../../../firebase";
import {
  chunkList,
  GEO_BIN_PRECISION,
  PUBLIC_GEO_BINS_COLLECTION,
  PUBLIC_GEO_BIN_SHARDS_COLLECTION,
} from "../../locations/geoBinMapShared";

const getBinMemberCount = (bin = {}) =>
  Number(bin?.counts?.totalSeniors ?? bin?.counts?.totalMembers ?? 0);

const normalizePublicDemandPoint = (point = {}) => {
  const lat = Number(point?.lat);
  const lng = Number(point?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    ...point,
    pointId:
      String(point?.pointId || point?.id || "").trim() ||
      `${lat.toFixed(5)}:${lng.toFixed(5)}`,
    lat,
    lng,
    status: String(point?.status || "").trim(),
  };
};

export async function getNearbyPublicDemandBins({
  latitude,
  longitude,
  includePoints = false,
  pointThreshold = 0,
} = {}) {
  const lat = Number(latitude);
  const lng = Number(longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return {
      centerBinId: "",
      nearbyBinIds: [],
      nearbyBins: [],
      nearbyPoints: [],
      nearbyMemberCount: 0,
    };
  }

  const centerBinId = geohash.encode(lat, lng, GEO_BIN_PRECISION);
  const nearbyBinIds = Array.from(
    new Set([centerBinId, ...(geohash.neighbors(centerBinId) || [])].filter(Boolean)),
  );

  const snapshots = await Promise.all(
    chunkList(nearbyBinIds).map((group) =>
      getDocs(
        query(collection(db, PUBLIC_GEO_BINS_COLLECTION), where(documentId(), "in", group)),
      ),
    ),
  );

  const binMap = new Map();
  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((docSnap) => {
      binMap.set(docSnap.id, {
        id: docSnap.id,
        ...docSnap.data(),
      });
    });
  });

  const nearbyBins = nearbyBinIds.map((binId) => binMap.get(binId)).filter(Boolean);
  const nearbyMemberCount = nearbyBins.reduce(
    (sum, bin) => sum + getBinMemberCount(bin),
    0,
  );

  let nearbyPoints = [];
  if (includePoints && nearbyMemberCount > Number(pointThreshold || 0)) {
    const pointSnapshots = await Promise.all(
      chunkList(nearbyBinIds).map((group) =>
        getDocs(
          query(
            collection(db, PUBLIC_GEO_BIN_SHARDS_COLLECTION),
            where("geoBinId", "in", group),
          ),
        ),
      ),
    );

    nearbyPoints = pointSnapshots.flatMap((snapshot) =>
      snapshot.docs.flatMap((docSnap) =>
        Object.values(docSnap.data()?.points || {})
          .map(normalizePublicDemandPoint)
          .filter(Boolean),
      ),
    );
  }

  return {
    centerBinId,
    nearbyBinIds,
    nearbyBins,
    nearbyPoints,
    nearbyMemberCount,
  };
}
